import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { ChartProps } from './types';
import { getSeriesColor, getSeriesPattern } from './color-palette';
import { ChartPatternDefs } from './ChartPatternDefs';
import { ChartTooltip, TooltipData } from './ChartTooltip';

const MARGIN = { top: 20, right: 20, bottom: 60, left: 60 };
const WIDTH = 600;
const HEIGHT = 400;

/**
 * Stacked bar chart using D3.js for SVG rendering.
 * For a single-dimension dataset, renders stacked segments in one bar.
 * Each segment represents a dimension value.
 * Supports keyboard navigation, tooltips, and accessibility patterns.
 */
export function StackedBarChart({ data, dimensionLabel, measureLabel, onSegmentClick }: ChartProps): React.ReactElement {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  const handleSegmentInteraction = useCallback(
    (dimensionValue: string, measureValue: number, event: React.MouseEvent | React.FocusEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const target = event.currentTarget as HTMLElement;
      const targetRect = target.getBoundingClientRect();
      setTooltip({
        dimensionValue,
        measureValue,
        dimensionLabel,
        measureLabel,
        x: targetRect.left - rect.left + targetRect.width / 2,
        y: targetRect.top - rect.top,
      });
    },
    [dimensionLabel, measureLabel],
  );

  const hideTooltip = useCallback(() => setTooltip(null), []);

  // Compute stacked layout data
  const stackedSegments = React.useMemo(() => {
    const total = data.reduce((sum, d) => sum + d.measureValue, 0);
    let cumulative = 0;
    return data.map((d) => {
      const start = cumulative;
      cumulative += d.measureValue;
      return { ...d, start, end: cumulative, total };
    });
  }, [data]);

  useEffect(() => {
    if (!svgRef.current || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('g.chart-content').remove();

    const innerWidth = WIDTH - MARGIN.left - MARGIN.right;
    const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;

    const g = svg
      .append('g')
      .attr('class', 'chart-content')
      .attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    const total = data.reduce((sum, d) => sum + d.measureValue, 0);

    const xScale = d3
      .scaleBand()
      .domain(['Total'])
      .range([0, innerWidth])
      .padding(0.3);

    const yScale = d3
      .scaleLinear()
      .domain([0, total])
      .nice()
      .range([innerHeight, 0]);

    // X axis
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale))
      .attr('font-size', '12px');

    // Y axis
    g.append('g').call(d3.axisLeft(yScale)).attr('font-size', '12px');

    // Axis labels
    g.append('text')
      .attr('x', innerWidth / 2)
      .attr('y', innerHeight + MARGIN.bottom - 5)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('fill', 'currentColor')
      .text(dimensionLabel);

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -MARGIN.left + 15)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('fill', 'currentColor')
      .text(measureLabel);

    // Stacked segments
    const barX = xScale('Total') ?? 0;
    const barWidth = xScale.bandwidth();

    stackedSegments.forEach((segment) => {
      const color = getSeriesColor(segment.dimensionValue);
      const patternId = getSeriesPattern(segment.dimensionValue);
      const y = yScale(segment.end);
      const segmentHeight = yScale(segment.start) - yScale(segment.end);

      g.append('rect')
        .attr('x', barX)
        .attr('y', y)
        .attr('width', barWidth)
        .attr('height', segmentHeight)
        .attr('fill', color)
        .attr('stroke', 'white')
        .attr('stroke-width', 1);

      const patternGroup = g.append('g').attr('style', `color: ${color}`);
      patternGroup
        .append('rect')
        .attr('x', barX)
        .attr('y', y)
        .attr('width', barWidth)
        .attr('height', segmentHeight)
        .attr('fill', `url(#${patternId})`)
        .attr('opacity', 0.3);
    });
  }, [data, dimensionLabel, measureLabel, stackedSegments]);

  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground" role="status">
        No data available
      </div>
    );
  }

  const innerWidth = WIDTH - MARGIN.left - MARGIN.right;
  const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const total = data.reduce((sum, d) => sum + d.measureValue, 0);

  const xScale = d3
    .scaleBand()
    .domain(['Total'])
    .range([0, innerWidth])
    .padding(0.3);

  const yScale = d3
    .scaleLinear()
    .domain([0, total])
    .nice()
    .range([innerHeight, 0]);

  const barX = xScale('Total') ?? 0;
  const barWidth = xScale.bandwidth();

  return (
    <div ref={containerRef} className="relative" role="img" aria-label={`Stacked bar chart: ${dimensionLabel} by ${measureLabel}`}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="h-auto w-full max-w-[600px]"
        aria-hidden="true"
      >
        <ChartPatternDefs />
      </svg>
      {/* Keyboard-navigable segment overlays */}
      <div className="absolute inset-0" aria-label="Chart data points" role="list">
        {stackedSegments.map((segment) => {
          const segX = (MARGIN.left + barX) / WIDTH * 100;
          const segY = (MARGIN.top + yScale(segment.end)) / HEIGHT * 100;
          const segW = barWidth / WIDTH * 100;
          const segH = (yScale(segment.start) - yScale(segment.end)) / HEIGHT * 100;

          return (
            <button
              key={segment.dimensionValue}
              type="button"
              role="listitem"
              className="absolute border-0 bg-transparent p-0 outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              style={{
                left: `${segX}%`,
                top: `${segY}%`,
                width: `${segW}%`,
                height: `${segH}%`,
              }}
              tabIndex={0}
              aria-label={`${dimensionLabel}: ${segment.dimensionValue}, ${measureLabel}: ${segment.measureValue}`}
              onMouseEnter={(e) => handleSegmentInteraction(segment.dimensionValue, segment.measureValue, e)}
              onFocus={(e) => handleSegmentInteraction(segment.dimensionValue, segment.measureValue, e)}
              onMouseLeave={hideTooltip}
              onBlur={hideTooltip}
              onClick={() => onSegmentClick?.(segment.dimensionValue)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSegmentClick?.(segment.dimensionValue);
                }
              }}
            />
          );
        })}
      </div>
      <ChartTooltip tooltip={tooltip} />
    </div>
  );
}
